"use client";

import React, { useEffect, useRef } from "react";
import "./week-at-orane.css";

const valueChain = [
  { title: "Demand forecasts", detail: "Demand", icon: "▥" },
  { title: "Procurement & buying", detail: "Procurement", icon: "🛒" },
  { title: "Production & supply", detail: "Production", icon: "▤" },
  { title: "MOQ & lot sizes", detail: "MOQ", icon: "▦" },
  { title: "Lead time & delivery", detail: "Lead time", icon: "◷" },
  { title: "Safety stock", detail: "Safety", icon: "◇" },
  { title: "Channel & SKU mix", detail: "Channel", icon: "♧" },
];

const locations = [
  { title: "Plant", items: ["Production plan", "Line utilisation", "Changeovers"], icon: "▤" },
  { title: "Warehouse", items: ["Stock levels", "Ageing", "Transfers"], icon: "▣" },
  { title: "Channel", items: ["Retail", "Distributor", "Direct"], icon: "⌂" },
];

const drivers = [
  ["Forecast error", "Demand vs plan"],
  ["MOQ / buying behaviour", "Larger order sizes"],
  ["Slow-moving SKUs", "Lifecycle or assortment"],
  ["Production mix", "Change in product mix"],
  ["Channel inventory", "Push vs pull"],
];

const questions = [
  "Which SKUs created the increase?",
  "In which plants / warehouses?",
  "Is demand actually slowing?",
  "What purchasing decisions created it?",
  "How much cash can realistically be released?",
];

const flow = [
  { title: "Business Question", detail: "We start with a clear business question to focus the analysis and outcomes." },
  { title: "Data", detail: "We gather and organize relevant data from multiple sources." },
  { title: "Measures", detail: "We define the right measures and metrics to evaluate performance." },
  { title: "Causes", detail: "We use AI to identify key drivers and underlying causes." },
  { title: "Model & Recommendation", detail: "We apply models to identify patterns, predict outcomes and generate actionable recommendations." },
  { title: "Human Decision", detail: "You review the insight and make the final decision, with AI as your partner." },
];

/* ---------- Scroll reveal (bottom to top) ---------- */

function useScrollReveal() {
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const root = ref.current;
    if (!root) return;
    const items = root.querySelectorAll<HTMLElement>("[data-reveal]");

    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce || !("IntersectionObserver" in window)) {
      items.forEach((el) => el.classList.add("is-visible"));
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: "0px 0px -60px 0px" }
    );

    items.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  return ref;
}

const delay = (i: number, step = 80, max = 400) =>
  ({ "--ii-delay": `${Math.min(i * step, max)}ms` } as React.CSSProperties);

/* ---------- Components ---------- */

function SectionHeading({ number, eyebrow, title, description }: {
  number?: string; eyebrow?: string; title: string; description?: string;
}) {
  return (
    <div className="ii-section-heading" data-reveal>
      <div className="ii-heading-copy">
        {eyebrow && <span className="ii-eyebrow">{eyebrow}</span>}
        <h2>{title}</h2>
        {description && <p>{description}</p>}
      </div>
    </div>
  );
}

function InventoryTrend() {
  return (
    <div className="ii-trend-card" aria-label="Illustrative inventory is rising while sales remain flat" data-reveal style={delay(1, 120)}>
      <div className="ii-trend-lines">
        <div className="ii-trend-row">
          <strong>Inventory <span>↑</span></strong>
          <svg viewBox="0 0 320 62" role="img" aria-label="Inventory trend rising">
            <polyline points="4,48 52,39 102,36 152,20 202,12 250,6 316,2" />
          </svg>
        </div>
        <div className="ii-trend-row ii-sales">
          <strong>Sales <span>→</span></strong>
          <svg viewBox="0 0 320 62" role="img" aria-label="Sales trend staying mostly flat">
            <polyline points="4,34 52,31 102,31 152,33 202,31 250,32 316,30" />
          </svg>
        </div>
      </div>
      <div className="ii-trend-insight">
        <span className="ii-insight-mark">↗</span>
        <strong>Something else is changing.</strong>
        <p>Inventory is moving differently from sales.</p>
      </div>
    </div>
  );
}

function InventoryChart() {
  const inventory = [100, 103, 105, 110, 125, 136, 140, 151, 155, 162, 160, 170];
  const sales = [68, 70, 71, 72, 74, 73, 75, 74, 72, 78, 80, 81];
  return (
    <div className="ii-chart-card" data-reveal>
      <div className="ii-chart-title">
        <strong>Inventory vs Sales <small>(illustrative)</small></strong>
        <div className="ii-legend"><span><i className="ii-blue-dot" />Inventory</span><span><i className="ii-slate-dot" />Sales</span></div>
      </div>
      <div className="ii-chart">
        <div className="ii-y-labels"><span>200</span><span>150</span><span>100</span><span>50</span><span>0</span></div>
        <div className="ii-chart-main">
          <div className="ii-chart-grid"><i /><i /><i /><i /><i /></div>
          <svg viewBox="0 0 600 190" preserveAspectRatio="none" aria-label="Inventory line rises over the year while sales remain comparatively flat">
            <polyline className="ii-inventory-line" points={inventory.map((v, i) => `${i * 54},${178 - v * .88}`).join(" ")} />
            <polyline className="ii-sales-line" points={sales.map((v, i) => `${i * 54},${178 - v * .88}`).join(" ")} />
            {inventory.map((v, i) => <circle key={`i${i}`} cx={i * 54} cy={178 - v * .88} r="3.2" className="ii-inventory-point" />)}
            {sales.map((v, i) => <circle key={`s${i}`} cx={i * 54} cy={178 - v * .88} r="3" className="ii-sales-point" />)}
          </svg>
          <div className="ii-months">{["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"].map(m => <span key={m}>{m}</span>)}</div>
        </div>
      </div>
    </div>
  );
}

/* ---------- Page ---------- */

export default function WeekAtOranePage() {
  const ref = useScrollReveal();

  return (
    <main className="ii-page" ref={ref}>
      <section className="ii-hero" id="top">
        <div className="ii-hero-background" aria-hidden="true">
          <img src="/assets/images/inventory-hero.jpg" alt="" />
        </div>
        <div className="ii-hero-inner">
          <div className="ii-hero-copy">
            <h1 data-reveal style={delay(0)}>Why is inventory increasing<br />when <em>sales aren’t?</em></h1>
            <p data-reveal style={delay(1, 150)}>A practical investigation into why working capital can rise even when demand does not.</p>
          </div>
        </div>
      </section>

      <div className="ii-content-layout">
        <div className="ii-article">
          <section className="ii-article-section">
            <SectionHeading number="01" title="The Business Question" description="Inventory is going up. Sales are flat or declining. Why is this happening, and what can we do about it?" />
            <InventoryTrend />
          </section>

          <section className="ii-article-section">
            <SectionHeading number="02" title="Don’t Start with AI. Start with the Business." description="Inventory is the result of many moving parts. Even when demand is stable, a combination of factors can build up inventory." />
            <div className="ii-value-chain ii-alternating-timeline">
              <div className="ii-timeline-rail" aria-hidden="true" />
              {valueChain.map((item, index) => (
                <div
                  className={`ii-timeline-step ${index % 2 === 0 ? "is-top" : "is-bottom"}`}
                  key={item.title}
                  data-reveal
                  style={delay(index, 90, 540)}
                >
                  <div className="ii-timeline-card">
                    <span className="ii-timeline-number">{String(index + 1).padStart(2, "0")}</span>
                    <span className="ii-timeline-title">{item.title}</span>
                  </div>
                  <span className="ii-timeline-stem" aria-hidden="true" />
                  <span className="ii-timeline-dot" aria-hidden="true" />
                </div>
              ))}
            </div>
          </section>

          <section className="ii-article-section">
            <SectionHeading number="03" title="What We Look At" description="Inventory is not a single number. It lives across the value chain." />
            <div className="ii-inventory-map">
              <div className="ii-map-root" data-reveal>SKU</div>
              <div className="ii-map-connector" />
              <div className="ii-location-grid">
                {locations.map((loc, i) => (
                  <article className="ii-location-card" key={loc.title} data-reveal style={delay(i, 120)}>
                    <h3>{loc.title}</h3>
                    <div className="ii-location-icon">{loc.icon}</div>
                    <ul>{loc.items.map(item => <li key={item}>{item}</li>)}</ul>
                  </article>
                ))}
              </div>
              <div className="ii-map-down" />
              <div className="ii-inventory-node" data-reveal>INVENTORY</div>
              <div className="ii-map-bottom">
                <div data-reveal><b>▥</b>Sales</div>
                <span>↔</span>
                <div data-reveal style={delay(1, 120)}><b>▤</b>Cash</div>
              </div>
            </div>
          </section>

          <section className="ii-article-section">
            <SectionHeading number="04" title="The Data Tells a Clear Story" description="A simple view can reveal a complex problem." />
            <div className="ii-analysis-layout">
              <InventoryChart />
              <div className="ii-drivers" data-reveal style={delay(1, 150)}>
                <h3>Common Drivers</h3>
                {drivers.map(([title, desc], i) => (
                  <div className="ii-driver" key={title}>
                    <b>{i + 1}</b>
                    <p><strong>{title}</strong><span>{desc}</span></p>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </div>

        <div className="ii-full-sections">
          <section className="ii-article-section ii-section-five">
            <SectionHeading number="05" title="What Would I Do Monday Morning?" description="Five questions to start with." />
            <div className="ii-monday-layout">
              <ol className="ii-questions">
                {questions.map((q, i) => (
                  <li key={q} data-reveal style={delay(i, 90)}>
                    <b>{String(i + 1).padStart(2, "0")}</b>
                    <span>{q}</span>
                    <span className="ii-question-arrow" aria-hidden="true">↗</span>
                  </li>
                ))}
              </ol>
              <div className="ii-monday-image" data-reveal style={delay(1, 150)}>
                <img src="/assets/images/what-would-i-do-monday-morning.jpg" alt="Analyst reviewing business data on a dashboard" />
              </div>
            </div>
          </section>
          <section className="ii-article-section ii-section-six">
            <SectionHeading number="06" title="The Role of AI" description="AI doesn’t replace business thinking. It helps us go deeper, faster and with more confidence." />
            <div className="ii-ai-layout">
              <ol className="ii-ai-list">
                {flow.map((step, i) => (
                  <li key={step.title} data-reveal style={delay(i % 3, 110)}>
                    <span className="ii-ai-list-number">{String(i + 1).padStart(2, "0")}</span>
                    <strong className="ii-ai-list-text">{step.title}</strong>
                    <p className="ii-ai-list-detail">{step.detail}</p>
                  </li>
                ))}
              </ol>
            </div>
            <blockquote data-reveal>“The real value of AI is not in asking a question, but in helping you ask a better one.”<cite>— Orane Insights</cite></blockquote>
          </section>
          {/* <section className="ii-related">
            <span className="ii-related-label">RELATED</span><small>01 / 52</small>
            <div className="ii-related-icon">▦</div>
            <h3>Inventory Intelligence</h3>
            <p>Identify excess, slow-moving or obsolete inventory.</p>
            <a href="#contact">Explore <b>→</b></a>
          </section> */}
        </div>
      </div>

      <section className="ii-cta" id="contact">
  <img className="ii-cta-bg" src="/assets/images/52-real-problem-cta.jpg" alt="" aria-hidden="true" />
  <div className="ii-cta-copy">
    <span className="ii-eyebrow" data-reveal>A MORE VALUABLE TOMORROW. TOGETHER.</span>
    <h2 data-reveal style={delay(1, 120)}>Don’t Start With AI.<br />Start With a Business Problem.</h2>
    <p data-reveal style={delay(2, 120)}>Bring us a problem, a decision or an exception that matters. We’ll work backwards from the business outcome to the context, data and intelligence required.</p>
    <div className="ii-cta-actions" data-reveal style={delay(3, 120)}>
      <a href="/tellusyourproblem#tell-details">Tell Us Your Problem <b>→</b></a>
    </div>
  </div>
</section>
    </main>
  );
}